"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal, ListChecks, Code as Code2, Activity, CircleCheck as CheckCircle2, ArrowRight, Clock, UserCheck, ShieldCheck, FileCode2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const auditSteps = [{
  id: "step-1",
  stepNumber: "01",
  title: "Technical Assessment",
  illustrativeTiming: "Illustrative: 1–2 weeks (varies by site scale)",
  summary: "A comprehensive audit of crawlability, indexation, performance, and structural health using Google Search Console, analytics, dedicated crawling tools, Core Web Vitals field reports, and raw server logs.",
  del1: "Full crawl & indexation audit across desktop and mobile Googlebot user-agents",
  del2: "Server access log inspection to analyze crawl frequency, status codes & waste",
  del3: "Site architecture, internal link distribution, canonicalisation & sitemaps review",
  del4: "Search Console data synthesis with Core Web Vitals field performance baseline",
  outcome: "A complete diagnostic blueprint documenting all structural, indexation, and crawl blockers."
}, {
  id: "step-2",
  stepNumber: "02",
  title: "Issue Diagnosis",
  illustrativeTiming: "Illustrative: 3–5 days (varies by complexity)",
  summary: "Moving from 'what is broken' to 'why'. I group technical issues into key architectural disciplines and evaluate log files to see how search engines actually navigate and render your domain.",
  del1: "Categorisation across crawlability, indexation, architecture & rendering",
  del2: "JavaScript rendering, client-side hydration & mobile usability diagnosis",
  del3: "Structured data schema validation, HTTPS headers, and security checks",
  del4: "Log file verification to isolate crawl budget waste on low-value URLs",
  outcome: "Clear, actionable understanding of underlying technical root causes rather than superficial symptoms."
}, {
  id: "step-3",
  stepNumber: "03",
  title: "Prioritisation",
  illustrativeTiming: "Illustrative: 2–4 days (aligned with dev sprints)",
  summary: "Ranking fixes by SEO impact, business importance, regression risk, and engineering effort to produce a realistic, developer-ready roadmap that fits your team's development cycles.",
  del1: "Commercial SEO impact versus engineering effort scoring matrix",
  del2: "Severity tiering (Critical blockers, High priority, Medium, Low)",
  del3: "Developer-ready technical specifications formatted for Jira, GitHub, or Linear",
  del4: "Direct scoping call with your technical leads and engineering teams",
  outcome: "A pragmatic engineering roadmap structured for rapid, friction-free developer execution."
}, {
  id: "step-4",
  stepNumber: "04",
  title: "Implementation & Testing",
  illustrativeTiming: "Illustrative: 2–4 weeks (depends on dev resources)",
  summary: "Detailed technical recommendations with clear acceptance criteria and testing notes, staging environment testing, Core Web Vitals and page speed optimisations, and post-deployment validation.",
  del1: "Staging environment testing with explicit acceptance criteria & QA notes",
  del2: "Core Web Vitals & page speed tuning (LCP, INP, CLS & critical rendering path)",
  del3: "Redirect validation, canonical rules & edge server caching configurations",
  del4: "Post-deployment verification via fresh crawls and Google Search Console URL inspection",
  outcome: "Safe, error-free production deployment with verified page speed gains and zero search disruption."
}, {
  id: "step-5",
  stepNumber: "05",
  title: "Monitoring & Improvement",
  illustrativeTiming: "Iterative & continuous cadence",
  summary: "Ongoing monitoring of crawl stats, index coverage, Core Web Vitals, and search rankings. Technical SEO is an iterative process of refinement, not a one-off task.",
  del1: "Continuous monitoring of Google Search Console crawl stats & index coverage",
  del2: "Ongoing tracking of real-user Core Web Vitals field data against CrUX standards",
  del3: "Automated recurring crawls to catch unintended code regressions early",
  del4: "Iterative strategic adjustments reflecting Google search algorithm updates",
  outcome: "Protected organic equity, resilient technical health, and compounding search performance over time."
}];
const highlights = [{
  title: "Hands-On Technical Analysis",
  desc: "Every server log, rendering script, and crawl directive is thoroughly reviewed for root-cause diagnosis.",
  icon: UserCheck
}, {
  title: "Zero PDF Bloat",
  desc: "I deliver prioritized, engineering-ready tickets instead of generic 100-page automated reports.",
  icon: FileCode2
}, {
  title: "Safety-First Implementation",
  desc: "All code recommendations are tested on staging to safeguard existing organic traffic.",
  icon: ShieldCheck
}];
export default function Auditprocess() {
  const [activeStep, setActiveStep] = useState(0);
  const getStepIcon = (index: number) => {
    switch (index) {
      case 0:
        return Search;
      case 1:
        return SlidersHorizontal;
      case 2:
        return ListChecks;
      case 3:
        return Code2;
      case 4:
        return Activity;
      default:
        return Search;
    }
  };
  return <section id="technicalauditprocess" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 size-96 rounded-full bg-secondary/30 blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 size-96 rounded-full bg-primary/5 blur-3xl opacity-50 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold px-3 py-1">
            HANDS-ON TECHNICAL SEO
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground mb-6">
            5-Stage Technical SEO Process
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            From initial assessment to continuous monitoring, I examine your server logs, architecture, and crawl data to deliver a clear, high-impact technical roadmap.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <div data-index={i} key={item.title}>
                <Card className="border border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
                      <Icon className="size-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-base mb-1">
                        {item.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="hidden lg:grid grid-cols-5 gap-3 mb-12">
          {auditSteps.map((step, i) => {
          const Icon = getStepIcon(i);
          const isActive = activeStep === i;
          return <div data-index={i} key={step.id}>
                <Button variant={isActive ? "default" : "outline"} className={`w-full h-auto py-4 px-3 flex flex-col items-center gap-2 rounded-xl transition-all ${isActive ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-foreground hover:bg-muted border-border"}`} onClick={() => setActiveStep(i)}>
                  <span className="text-xs font-mono font-semibold opacity-80">
                    STAGE {step.stepNumber}
                  </span>
                  <div className="flex items-center gap-2">
                    <Icon className="size-4 shrink-0" />
                    <span className="text-xs font-semibold truncate">
                      {step.title}
                    </span>
                  </div>
                </Button>
              </div>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-4">
            {auditSteps.map((step, i) => {
            const Icon = getStepIcon(i);
            const isActive = activeStep === i;
            return <div data-index={i} key={step.id}>
                  <Card className={`cursor-pointer transition-all duration-200 border ${isActive ? "border-primary bg-primary/5 shadow-md" : "border-border bg-card hover:border-primary/40"}`} onClick={() => setActiveStep(i)}>
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className={`size-10 rounded-lg flex items-center justify-center shrink-0 font-semibold text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {step.stepNumber}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-foreground text-base flex items-center gap-2">
                            <Icon className="size-4 text-primary shrink-0" />
                            <span className="truncate">{step.title}</span>
                          </h3>
                        </div>
                        <p className="text-muted-foreground text-xs line-clamp-2 leading-relaxed mb-2">
                          {step.summary}
                        </p>
                        <Badge variant="outline" className="text-[11px] text-muted-foreground border-border font-normal">
                          <Clock className="size-3 mr-1" />
                          {step.illustrativeTiming}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>

          <div className="lg:col-span-7">
            <motion.div key={activeStep} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border border-border bg-card shadow-lg rounded-2xl overflow-hidden">
                <div className="bg-primary/10 border-b border-border p-6 md:p-8 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <span className="text-xs font-mono font-bold text-primary tracking-wider uppercase block mb-1">
                      Stage {auditSteps[activeStep].stepNumber} of 05
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                      {auditSteps[activeStep].title}
                    </h3>
                  </div>
                  <Badge variant="secondary" className="px-3 py-1.5 text-xs font-semibold">
                    <Clock className="size-3.5 mr-1.5" />
                    Timing: Scope Dependent
                  </Badge>
                </div>

                <CardContent className="p-6 md:p-8 space-y-6">
                  <div className="p-3.5 rounded-lg bg-muted/60 border border-border text-xs text-muted-foreground flex items-start gap-2.5">
                    <Clock className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong>Timeline note:</strong> Duration depends on website size, platform, technical complexity, severity of issues, and development resources. Timeframes shown ({auditSteps[activeStep].illustrativeTiming}) are illustrative examples only, never rigid deadlines.
                    </span>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                      Overview
                    </h4>
                    <p className="text-foreground text-base leading-relaxed">
                      {auditSteps[activeStep].summary}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">
                      Key Deliverables & Actions
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[auditSteps[activeStep].del1, auditSteps[activeStep].del2, auditSteps[activeStep].del3, auditSteps[activeStep].del4].map((item, idx) => <div data-index={idx} key={idx} className="flex items-start gap-2.5 p-3 rounded-lg bg-muted/50 border border-border/50">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span className="text-xs font-medium text-foreground leading-snug">
                            {item}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <Separator />

                  <div className="bg-secondary/40 border border-secondary rounded-xl p-4 md:p-5">
                    <h4 className="text-xs font-bold text-secondary-foreground uppercase tracking-wider mb-1">
                      Expected Business Outcome
                    </h4>
                    <p className="text-sm font-medium text-foreground">
                      {auditSteps[activeStep].outcome}
                    </p>
                  </div>

                  <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
                    <div className="text-xs text-muted-foreground font-medium">
                      Technical SEO is delivered as an iterative engineering partnership, not an automated one-off report.
                    </div>
                    <div className="flex gap-2">
                      {activeStep > 0 && <Button variant="outline" size="sm" onClick={() => setActiveStep(prev => prev - 1)}>
                          Previous Stage
                        </Button>}
                      {activeStep < auditSteps.length - 1 && <Button size="sm" onClick={() => setActiveStep(prev => prev + 1)}>
                          Next Stage
                          <ArrowRight className="size-3.5" />
                        </Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 md:mt-24">
          <Card className="border border-border bg-card shadow-lg rounded-2xl overflow-hidden text-card-foreground">
            <div className="p-8 md:p-12 text-center max-w-3xl mx-auto space-y-6">
              <Badge variant="secondary" className="px-3 py-1 text-xs">
                PERSONAL TECHNICAL OVERSIGHT
              </Badge>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Ready to fix your site's technical search blockers?
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed">
                Book a free 30-minute technical SEO consultation directly with me. I will evaluate your domain and discuss immediate growth opportunities.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                <Button asChild size="lg" className="rounded-md font-medium">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                  <Link to="/CaseStudiesPage">Explore My Case Studies</Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}